import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import "./Purchases.css";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";
const SUPPLIER_API = `${API_BASE}/suppliers`;
const PURCHASE_API = `${API_BASE}/purchases`;

export default function SupplierPurchaseHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { supplierId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState("ALL");

  const supplierName = location.state?.supplier_name || supplierInfo?.supplier_name || "Supplier";

  const fetchData = async () => {
    if (!supplierId) {
      setError("Invalid supplier");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const [purchaseRes, supplierRes] = await Promise.allSettled([
        axios.get(`${PURCHASE_API}/supplier/${supplierId}`),
        axios.get(`${SUPPLIER_API}/${supplierId}`),
      ]);

      if (purchaseRes.status === "fulfilled") {
        const normalized = (purchaseRes.value?.data || [])
          .map((r) => {
            const lineType = String(r.line_type || "ITEM").toUpperCase();
            const qty = Number(r.quantity || 0);
            const rate = Number(r.rate || 0);
            const amount =
              Number(r.amount || 0) > 0
                ? Number(r.amount || 0)
                : lineType === "EXPENSE"
                  ? Number(r.amount || 0)
                  : qty * rate;

            return {
              ...r,
              line_type: lineType,
              quantity: qty,
              rate,
              amount: Number(amount || 0),
            };
          })
          .sort((a, b) => new Date(b.order_date || b.created_at) - new Date(a.order_date || a.created_at));

        setRows(normalized);
      } else {
        setRows([]);
        setError(
          purchaseRes.reason?.response?.data?.message || "Failed to load supplier purchase history"
        );
      }

      if (supplierRes.status === "fulfilled") {
        setSupplierInfo(supplierRes.value?.data || null);
      } else {
        setSupplierInfo(null);
      }
    } catch (err) {
      console.error("Supplier purchase history error:", err);
      setRows([]);
      setError(err?.response?.data?.message || "Failed to load supplier purchase history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [supplierId]);

  const filteredSourceRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    return rows.filter((row) => {
      const searchableText = [
        row.line_type,
        row.supplier_name,
        supplierInfo?.supplier_name,
        row.product_name,
        row.expense_name,
        row.order_group_id,
        row.order_id,
        row.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = !normalizedSearch || searchableText.includes(normalizedSearch);
      if (!matchesSearch) {
        return false;
      }

      if (dateRange === "ALL") {
        return true;
      }

      const sourceDate = row.order_date || row.created_at;
      const parsedDate = sourceDate ? new Date(sourceDate) : null;
      if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
        return false;
      }

      const rowTime = parsedDate.getTime();
      let rangeStart = null;
      let rangeEnd = endOfToday;

      if (dateRange === "TODAY") {
        rangeStart = startOfToday;
      } else if (dateRange === "YESTERDAY") {
        rangeStart = new Date(startOfToday);
        rangeStart.setDate(rangeStart.getDate() - 1);
        rangeEnd = new Date(startOfToday.getTime() - 1);
      } else if (dateRange === "WEEK") {
        rangeStart = new Date(startOfToday);
        rangeStart.setDate(rangeStart.getDate() - 6);
      } else if (dateRange === "MONTH") {
        rangeStart = new Date(startOfToday);
        rangeStart.setMonth(rangeStart.getMonth() - 1);
      } else if (dateRange === "YEAR") {
        rangeStart = new Date(startOfToday);
        rangeStart.setFullYear(rangeStart.getFullYear() - 1);
      }

      return rowTime >= rangeStart.getTime() && rowTime <= rangeEnd.getTime();
    });
  }, [rows, searchTerm, dateRange, supplierInfo]);

  const tableRows = useMemo(() => {
    const groupedMap = new Map();

    for (const row of filteredSourceRows) {
      const groupKey = row.order_group_id || row.order_id || `ROW-${row.id}`;

      if (!groupedMap.has(groupKey)) {
        groupedMap.set(groupKey, {
          groupKey,
          orderRef: row.order_group_id || row.order_id || `PUR-${String(row.id).padStart(6, "0")}`,
          supplier_name: supplierInfo?.supplier_name || row.supplier_name || supplierName || "-",
          order_date: row.order_date,
          created_at: row.created_at,
          items: [],
          expenses: [],
        });
      }

      const group = groupedMap.get(groupKey);
      if (row.line_type === "EXPENSE") {
        group.expenses.push(row);
      } else {
        group.items.push(row);
      }
    }

    const mergedRows = [];

    for (const group of groupedMap.values()) {
      const itemLines = group.items.length ? group.items : [null];

      for (let index = 0; index < itemLines.length; index += 1) {
        const item = itemLines[index];
        const expense = group.expenses[index] || null;
        const productAmount = Number(item?.amount || 0);
        const expenseAmount = Number(expense?.amount || 0);

        mergedRows.push({
          key: `${group.groupKey}-${index}`,
          orderRef: group.orderRef,
          order_date: item?.order_date || group.order_date,
          created_at: item?.created_at || group.created_at,
          supplier_name: group.supplier_name,
          product_name: item?.product_name || "-",
          quantity: item ? Number(item.quantity || 0) : "-",
          rate: item ? Number(item.rate || 0) : null,
          product_amount: item ? productAmount : null,
          expense_name: expense?.expense_name || "-",
          expense_amount: expenseAmount,
          total: productAmount + expenseAmount,
        });
      }
    }

    return mergedRows.sort(
      (a, b) => new Date(b.order_date || b.created_at) - new Date(a.order_date || a.created_at)
    );
  }, [filteredSourceRows, supplierInfo, supplierName]);

  const summary = useMemo(() => {
    const totalRows = filteredSourceRows.length;
    const itemRows = filteredSourceRows.filter((r) => r.line_type !== "EXPENSE");
    const expenseRows = filteredSourceRows.filter((r) => r.line_type === "EXPENSE");

    const totalQty = itemRows.reduce((sum, r) => sum + Number(r.quantity || 0), 0);
    const totalItemAmount = itemRows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const totalExpenseAmount = expenseRows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const grandTotal = totalItemAmount + totalExpenseAmount;

    const firstDate = filteredSourceRows.length
      ? filteredSourceRows.reduce((min, r) => {
        const d = new Date(r.order_date || r.created_at || 0);
        return d < min ? d : min;
      }, new Date(filteredSourceRows[0].order_date || filteredSourceRows[0].created_at || 0))
      : null;

    const lastDate = filteredSourceRows.length
      ? filteredSourceRows.reduce((max, r) => {
        const d = new Date(r.order_date || r.created_at || 0);
        return d > max ? d : max;
      }, new Date(filteredSourceRows[0].order_date || filteredSourceRows[0].created_at || 0))
      : null;

    return {
      totalRows,
      totalQty,
      totalItemAmount,
      totalExpenseAmount,
      grandTotal,
      firstDate,
      lastDate,
    };
  }, [filteredSourceRows]);

  return (
    <div className="page-container">
      <div className="purchase-top-bar">
        <button
          id="purchase-history-sales-style-back-btn"
          type="button"
          className="back-btn purchase-history-sales-back-btn"
          onClick={() => navigate("/purchase/history")}
        >
          Back
        </button>
        <h2>{supplierName} - Till Date Purchase Details</h2>
        <button className="edit-btn" onClick={fetchData} style={{ marginLeft: "auto" }}>
          Refresh
        </button>
      </div>

      <div className="inventory-card">
        {error && (
          <div style={{ marginBottom: 12, color: "#dc2626", fontWeight: 700 }}>
            {error}
          </div>
        )}

        {supplierInfo && (
          <div style={{ marginBottom: 12, fontWeight: 600 }}>
            Supplier: {supplierInfo.supplier_name} | Mobile: {supplierInfo.mobile || "-"} | Email:{" "}
            {supplierInfo.email || "-"}
          </div>
        )}

        <div className="supplier-history-filters">
          <input
            type="text"
            className="supplier-history-search"
            placeholder="Search type, ref, supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="supplier-history-range"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="ALL">All Time</option>
            <option value="TODAY">Today</option>
            <option value="YESTERDAY">Yesterday</option>
            <option value="WEEK">1 Week</option>
            <option value="MONTH">1 Month</option>
            <option value="YEAR">1 Year</option>
          </select>
        </div>

        <div style={{ marginBottom: 14, fontWeight: 700, color: "#1e40af" }}>
          Entries: {summary.totalRows} | Total Qty: {summary.totalQty} | Item Total: ₹
          {summary.totalItemAmount.toFixed(2)} | Expense Total: ₹{summary.totalExpenseAmount.toFixed(2)} |
          Grand Total: ₹{summary.grandTotal.toFixed(2)} | From:{" "}
          {summary.firstDate ? summary.firstDate.toLocaleDateString("en-IN") : "-"} | To:{" "}
          {summary.lastDate ? summary.lastDate.toLocaleDateString("en-IN") : "-"}
        </div>

        <table className="inventory-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Date</th>
              <th>Supplier</th>
              <th>Product</th>
              <th>Quantity</th>
              <th>Rate</th>
              <th>Product Amt</th>
              <th>Expense</th>
              <th>Expense Amt</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="10" className="empty-row">
                  Loading supplier history...
                </td>
              </tr>
            )}

            {!loading && tableRows.length === 0 && (
              <tr>
                <td colSpan="10" className="empty-row">
                  No purchase records found
                </td>
              </tr>
            )}

            {!loading &&
              tableRows.map((r) => (
                <tr key={r.key}>
                  <td>{r.orderRef}</td>
                  <td>
                    {(r.order_date || r.created_at)
                      ? new Date(r.order_date || r.created_at).toLocaleDateString("en-IN")
                      : "-"}
                  </td>
                  <td>{r.supplier_name}</td>
                  <td>{r.product_name}</td>
                  <td>{r.quantity}</td>
                  <td>{r.rate === null ? "-" : `₹${r.rate.toFixed(2)}`}</td>
                  <td>{r.product_amount === null ? "-" : `₹${r.product_amount.toFixed(2)}`}</td>
                  <td>{r.expense_name}</td>
                  <td>₹{r.expense_amount.toFixed(2)}</td>
                  <td>₹{r.total.toFixed(2)}</td>
                </tr>
              ))}

            {!loading && tableRows.length > 0 && (
              <tr>
                <td colSpan="4" style={{ textAlign: "center", fontWeight: 700 }}>Totals</td>
                <td style={{ fontWeight: 700 }}>{summary.totalQty}</td>
                <td style={{ textAlign: "center", fontWeight: 700 }}>-</td>
                <td style={{ fontWeight: 700 }}>₹{summary.totalItemAmount.toFixed(2)}</td>
                <td style={{ textAlign: "center", fontWeight: 700 }}>-</td>
                <td style={{ fontWeight: 700 }}>₹{summary.totalExpenseAmount.toFixed(2)}</td>
                <td style={{ fontWeight: 700 }}>₹{summary.grandTotal.toFixed(2)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
